import { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { Add, HelpOutline } from "@mui/icons-material";
import { debounce } from "lodash";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LogoutContainer from "../Layout/LogoutContainer";
import {
  StyledHeader,
  HeaderLeftContent,
  HeaderRightContent,
  PageTitleSubtitle,
} from "../Common/styles/header";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { openHelp } from "../../Redux/reducers/helpSlice";
import { platformAdminPermissions } from "../../utils/roles";
import StudiesList from "./StudiesList";

const AllStudies = () => {
  const { t: sidebarT } = useTranslation("sidebar");
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(searchParams.get("query") ?? "");
  const [openDrawer, setOpenDrawer] = useState(false);
  const platformPermissions = useAppSelector(
    (state) => state.user.platformPermissions,
  );

  const canEdit = useMemo(
    () => platformPermissions.includes(platformAdminPermissions.managePlatform),
    [platformPermissions],
  );
  const canViewPlatform = useMemo(
    () => platformPermissions.includes(platformAdminPermissions.viewPlatform),
    [platformPermissions],
  );

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchText) {
      params.set("query", searchText);
    }
    setSearchParams(params, { replace: true });
  }, [setSearchParams, searchText]);

  const modifySearchTerm = useMemo(
    () =>
      debounce((val: string) => {
        setSearchText(val);
      }, 500),
    [],
  );

  return (
    <>
      <StyledHeader>
        <Box sx={HeaderLeftContent}>
          <PageTitleSubtitle
            title="All Studies"
            subtitle="View and manage all platform studies."
          />
        </Box>
        <Box sx={HeaderRightContent}>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDrawer(true)}
            >
              New Study
            </Button>
          )}
          {canViewPlatform && (
            <Tooltip title={sidebarT("HELP_TOOLTIP")}>
              <IconButton onClick={() => dispatch(openHelp("studies"))} size="small">
                <HelpOutline />
              </IconButton>
            </Tooltip>
          )}
          <LogoutContainer />
        </Box>
      </StyledHeader>
      <Box sx={{ height: "calc(100% - 92px)", p: 5, overflow: "auto" }}>
        <StudiesList
          searchText={searchText}
          modifySearchTerm={(val: string) => modifySearchTerm(val)}
          openDrawer={openDrawer}
          setOpenDrawer={setOpenDrawer}
          canEdit={canEdit}
          type="all-studies"
        />
      </Box>
    </>
  );
};

export default AllStudies;
